import { createRouter, createWebHistory } from 'vue-router';
import { token, userToken } from "@/stores/token";
import { projectFirestore } from '../firebase/config';
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import dayjs from "dayjs";
// Phase 1 (2026-08-17, trial-countdown correctness) — this file no longer
// parses the trial date itself (routed through shared/trial below, which
// does its own explicit DD/MM/YYYY component split and never needs this
// plugin). customParseFormat.extend() is kept here regardless: this module
// loads at app bootstrap, before any other component, and dayjs plugins
// mutate a single shared singleton — a wide audit while building this fix
// found DOZENS of other call sites across src/ (Calendar.vue, ToggleShifts.vue,
// DashboardView.vue, Timesheets.vue, EditShift.vue, ViewDashboardShifts.vue,
// and more) that call the two-arg `dayjs(str, "DD/MM/YYYY")` form WITHOUT
// extending the plugin themselves — they are silently relying on THIS line
// having already run. Removing it would break date parsing app-wide, not
// just here. Flagged as a real fragility worth its own ticket (every one of
// those call sites has an undeclared dependency on this file's load order);
// out of scope to fix in this phase.
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { useAuth0 } from '@auth0/auth0-vue';
import { watch } from 'vue';
import { useScreenGuard } from '@/views/screenGuard';
import { ensureFirebaseAuth } from '@/auth/firebaseBridge';
import { auth0, ensureSessionRestored } from '@/auth/auth0';
import { SHARED_TERMINAL_IDLE_TIMEOUT_MS } from '@/composables/useIdleLogout';
import { trace, redactUrl } from '@/utils/authTrace';
import { markBootHealthy } from '@/utils/swWatchdog';
import { trialDaysLeft as computeTrialDaysLeft } from '../../shared/trial';

// Cache for guard Firestore results. Populated after a successful guard pass
// and cleared on logout (via clearGuardCache) or when a new-user setup flow
// is detected. This avoids 3–4 serial Firestore round-trips on every navigation.
let guardCache = null; // { newUser, mustChangePassword, accountStatus, trialDaysLeft, allowedRoutes }

export const clearGuardCache = () => {
  guardCache = null;
};

// Map view names (stored in Firestore) to route names (used in router).
//
// ── A VALUE MAY BE ONE ROUTE NAME OR AN ARRAY OF THEM. Widened 2026-10-13. ──
//
// It was one-to-one, and that made a SECOND route gated on an EXISTING view
// impossible to express: `allowedRoutes` simply would not contain it, the guard
// would bounce every user to the Dashboard, and the sidebar would still show
// the entry — because canView() tests the VIEW list while this tests the view
// list MAPPED THROUGH HERE. A visible menu item that always redirects, with no
// error and nothing failing in any suite.
//
// That is not hypothetical. `CheckoutResults` had no entry here at all, and the
// page had NEVER mounted, for anyone, in any account state — see the comment at
// the CheckoutResults branch below and src/tests/checkoutResultsRouteReachable.test.js.
//
// The alternative was a new `Integrations` view name, and it measured worse: a
// data migration across every organisation's permission groups PLUS two more
// hardcoded view lists (OrgSettings.vue's and Setup.vue's permission-group
// editors). A view name already lives in five places; this adds none.
//
// WHY ARRAYS RATHER THAN ONE ROUTE PER VIEW. The capability a venue grants is
// "may this person connect and feed a payroll system". Export Timesheets, the
// Integrations page and (phase 3) the Xero mapping screen are three surfaces of
// that ONE capability, not three capabilities. Splitting them would ask a venue
// to answer a question they have no basis to answer differently.
//
// brief-integrations-page.md D-C, and tickets/ticket-nav-filter-and-route-guard-diverge.md
// for the divergence this does NOT fix.
// Exported for the nav/guard agreement invariant
// (src/tests/integrationsNavAndRoute.test.js). The sidebar and this map are two
// artifacts answering one question, and the test can only disagree with them if
// it reads the real one rather than a copy.
export const viewNameToRouteMap = {
  'Dashboard': 'dashboard',
  // T2 (2026-10-27) — the chat page is the News view's second route, exactly as
  // `Export Timesheets` already covers four. One capability, because it is the
  // same product area: a person who may read the feed may read the chat, and a
  // separate permission would be a thing somebody has to grant before T6 can
  // repoint the feed at it. `src/tests/integrationsNavAndRoute.test.js` asserts
  // every route named here is a real route, which is why the two land together.
  'News': ['comms', 'comms-chat'],
  'Schedule': 'schedule',
  'Locations': 'locations',
  'People': 'people',
  // H1 (2026-11-01, tickets/retired/brief-help-page.md): Help & support is its own page at /help and /help/<chapter>;
  // /support and /support/help/<chapter> redirect there. One capability, four route names.
  'Support': ['help', 'help-chapter', 'support', 'support-help'],
  'Settings': 'OrgSettings',
  'Reports': 'reports',
  'Forms': 'forms',
  'Templates': 'templates',
  'Timesheets': 'timesheets',
  'Export Timesheets': ['export-timesheets', 'integrations', 'integrations-xero', 'send-to-xero'],
  'Submitted': 'submitted'
};

// Normalises a map value to an array, so the twelve string entries above keep
// working unchanged. Written as a function rather than by rewriting every value
// as a one-element array: twelve edits to express "nothing changed" is twelve
// chances to mistype a route name, and a diff in which only one line is a real
// change is a diff somebody can review.
export const routeNamesForView = (viewName) => {
  const mapped = viewNameToRouteMap[viewName];
  if (!mapped) return [];
  return Array.isArray(mapped) ? mapped : [mapped];
};

const checkUserVerification = async (user) => {
  const orgQuery = query(
    collection(projectFirestore, "organisations"),
    where("uid", "array-contains", user.sub)
  );

  const orgSnapshot = await getDocs(orgQuery);
  const matchingOrgs = orgSnapshot.docs;

  if (matchingOrgs.length > 0) {
    const orgId = matchingOrgs[0].id;
    const peopleQuery = query(
      collection(projectFirestore, `organisations/${orgId}/people`),
      where("uid", "==", user.sub)
    );

    const peopleSnapshot = await getDocs(peopleQuery);
    const matchingPeople = peopleSnapshot.docs;

    if (matchingPeople.length > 0) {
      const personData = matchingPeople[0].data();
      return personData.emailVerified;
    }
  }

  return false;
};

/**
 * combinedGuard — async navigation guard (Promise-based return).
 *
 * All guard logic lives inside runChecks(). The guard calls runChecks()
 * directly when Auth0 has already finished loading, and schedules it via
 * a watcher otherwise. This guarantees that runChecks() is ALWAYS
 * executed — there is no code path that calls next() / returns true
 * unconditionally without performing every check first.
 *
 * Check priority order:
 *   1. Authentication (isAuthenticated, organisationId)
 *   2. New-user / setup  ← highest priority among data checks
 *   3. Account / trial status
 *   4. Per-route permission (userAccess)
 */
// Exported (2026-08-08) so the no-org bounce can be unit-tested directly,
// same rationale as sessionRestoreGuard's own export below.
export const combinedGuard = (to, from) => {
  return new Promise((resolve) => {
    trace('combined-guard-entered', { to: redactUrl(to.fullPath), from: redactUrl(from.fullPath) || '(initial)' });
    const store = token();
    const uT = userToken();
    const { isAuthenticated, isLoading, user } = useAuth0();

    const runChecks = async () => {
      // --- Authentication checks (always live — never cached) ---

      // #73 (2026-08-03): a once-per-app-load fallback for the plugin's own
      // cookie-gated boot restore — see the comment on ensureSessionRestored
      // in auth0.js for why isAuthenticated can still be false here despite
      // a valid cached refresh token. No-ops instantly on every navigation
      // after the first (the module-level flag), and no-ops instantly here
      // too if isAuthenticated is already true.
      await ensureSessionRestored();

      if (!isAuthenticated.value) {
        resolve({ name: 'login' });
        return;
      }

      // Orgless-user white-screen investigation (2026-08-08) — this used to
      // resolve to 'login', which sessionRestoreGuard's OWN bounce (below)
      // then sent straight back to 'dashboard' on the very next navigation
      // (it only checked isAuthenticated, not org membership) — an
      // unconditional login<->dashboard ping-pong with neither guard ever
      // able to break it. 'no-organisation' is guarded by isAuthenticated
      // ONLY (noOrganisationGuard, this file), so landing there is terminal
      // — no guard downstream of it bounces an authenticated user away.
      //
      // Tri-state fix (2026-08-09) — organisationId === '' does not
      // distinguish "lookup hasn't run yet" from "lookup ran, no org
      // found", so this branch used to fire a terminal no-organisation
      // decision during the transient window between a fresh login and
      // handleUserLogin's org query actually completing (a real sysadmin
      // could be caught here). 'unknown' routes to post-login instead —
      // unguarded, and its own onMounted runs handleUserLogin, which is
      // the only place that actually performs the lookup.
      if (store.orgResolution === 'unknown') {
        resolve({ name: 'post-login' });
        return;
      }

      if (store.orgResolution === 'resolved-none') {
        resolve({ name: 'no-organisation' });
        return;
      }

      // --- Firebase Auth bridge (must run before ANY Firestore read) ---
      // Establishes the Firebase Auth session from the Auth0 login so the
      // enforced Firestore rules see request.auth on the guard's own getDocs()
      // below and on every component/composable that mounts after navigation.
      // Awaited even on the cache fast-path, because components still read.
      // Membership-revocation fix (2026-08-24) — a FAILED bridge makes every
      // Firestore read below fail with the SAME 'permission-denied' code that a
      // genuinely revoked membership produces (no request.auth at all, vs. a
      // request.auth that is simply no longer in the org's uid array). Recorded
      // here so the account-status catch can tell the two apart: a mint outage
      // must bounce users to login to re-authenticate, and must never strand
      // the entire customer base on the terminal no-organisation screen with
      // their persisted org state wiped.
      let firebaseBridgeFailed = false;
      try {
        await ensureFirebaseAuth();
      } catch (error) {
        firebaseBridgeFailed = true;
        // If the bridge fails (e.g. mint function down), Firestore reads will be
        // denied. Log loudly and fall through — the reads below will then fail
        // and resolve to login rather than silently exposing data.
        console.error('Firebase auth bridge failed in combinedGuard:', error);

        // P2 (2026-08-24, retired-staff authz Phase 1). mintFirebaseToken now
        // refuses anyone with no ACTIVE membership anywhere — including a
        // retired person — with a 403. That is terminal in a way no other mint
        // failure is: falling through would deny the people query a few lines
        // below, whose catch resolves to 'login', where Auth0 (which knows
        // nothing about retirement) authenticates them again, the guard re-runs,
        // the mint 403s again... an infinite login bounce with nothing on screen
        // explaining why.
        //
        // Handled here rather than in the catches below because at 403 the
        // outcome is already known and every subsequent read is guaranteed to
        // fail. Only 403 short-circuits; a 500 or a network failure still falls
        // through to the existing login path, because those ARE recoverable and
        // must never strand the whole customer base on the terminal screen.
        //
        // The three store writes mirror the membership-revocation branch below:
        // noOrganisationGuard bounces an orgResolution of 'resolved-org' straight
        // back to the dashboard, so redirecting without downgrading it recreates
        // the 2026-08-08 infinite guard cycle.
        if (error && error.status === 403) {
          trace('combined-guard-mint-forbidden', { to: redactUrl(to.fullPath) });
          guardCache = null;
          store.organisationIdChange('');
          store.orgResolutionChange('resolved-none');
          uT.permissionsChange('');
          // Dev-test defect 1 (2026-08-24) — carry the reason through, so a
          // retired person gets "your access was removed" rather than the
          // generic "you aren't linked to an organisation". Same query
          // parameter usePostLoginHandler sets; NoOrganisation.vue reads it.
          resolve({
            name: 'no-organisation',
            ...(error.code === 'RETIRED' ? { query: { reason: 'retired' } } : {}),
          });
          return;
        }
      }

      // --- Fast path: use cached Firestore results when available ---

      if (guardCache) {
        const { newUser, mustChangePassword, accountStatus, trialDaysLeft, allowedRoutes } = guardCache;

        if (newUser) {
          // Clear cache so the setup flow always re-checks after onboarding
          guardCache = null;
          if (to.name !== 'setup') {
            resolve({ name: 'setup' });
            return;
          }
          resolve(true);
          return;
        }

        if (to.name === 'setup') {
          resolve({ name: 'dashboard' });
          return;
        }

        if (mustChangePassword) {
          // Clear cache so the flow always re-checks after the password change
          guardCache = null;
          if (to.name !== 'change-password') {
            resolve({ name: 'change-password' });
            return;
          }
          resolve(true);
          return;
        }

        if (to.name === 'change-password') {
          resolve({ name: 'dashboard' });
          return;
        }

        // CheckoutResults — see the full-path branch below for why this exists
        // and why it is handled here rather than exempted from the guard.
        // BOTH paths need it: this cached one runs on in-app navigations, the
        // uncached one on the fresh page load Stripe's redirect produces.
        if (to.name === 'CheckoutResults') {
          resolve(true);
          return;
        }

        if (accountStatus === 'inactive' || (accountStatus === 'trial' && trialDaysLeft <= 0)) {
          const isAdmin = uT.permissions === 'Systems Administrator';
          const allowedRoute = isAdmin ? 'OrgSettings' : 'dashboard';
          if (to.name !== allowedRoute) {
            resolve({ name: allowedRoute, ...(isAdmin ? { query: { tab: 'plan' } } : {}) });
          } else {
            resolve(true);
          }
          return;
        }

        if (!allowedRoutes) {
          resolve(to.name === 'dashboard' ? true : { name: 'dashboard' });
          return;
        }

        if (allowedRoutes.includes(to.name)) {
          resolve(true);
          return;
        }

        if (allowedRoutes.includes('dashboard')) {
          resolve({ name: 'dashboard' });
        } else if (allowedRoutes.length > 0) {
          resolve({ name: allowedRoutes[0] });
        } else {
          resolve({ name: 'login' });
        }
        return;
      }

      // --- Slow path: fetch from Firestore and populate cache ---

      // --- New-user / setup check (HIGHEST priority among data checks) ---

      let isNewUser = false;
      let mustChangePassword = false;

      try {
        const peopleQuery = query(
          collection(projectFirestore, `organisations/${store.organisationId}/people`),
          where("uid", "==", user.value.sub)
        );

        const peopleSnapshot = await getDocs(peopleQuery);

        if (peopleSnapshot.empty) {
          isNewUser = true;
        } else {
          const personData = peopleSnapshot.docs[0].data();
          isNewUser = personData.newUser === true;
          mustChangePassword = personData.mustChangePassword === true;
        }

        if (isNewUser) {
          // Do not cache — setup flow must re-check after onboarding completes
          if (to.name !== 'setup') {
            resolve({ name: 'setup' });
            return;
          }
          // newUser going to setup — allow immediately, skip account/permission checks
          // (no permissions exist in Firestore yet until setup is completed)
          resolve(true);
          return;
        }

        if (to.name === 'setup') {
          resolve({ name: 'dashboard' });
          return;
        }

        if (mustChangePassword) {
          // Do not cache — must re-check after the forced password change
          // completes (the Cloud Function clears the flag on a successful
          // self-service password set).
          if (to.name !== 'change-password') {
            resolve({ name: 'change-password' });
            return;
          }
          // mustChangePassword going to change-password — allow immediately,
          // skip account/permission checks (the user must not reach anything
          // else until the password is changed)
          resolve(true);
          return;
        }

        if (to.name === 'change-password') {
          resolve({ name: 'dashboard' });
          return;
        }
      } catch (error) {
        console.error('Error fetching person record in combinedGuard:', error);
        resolve({ name: 'login' });
        return;
      }

      // --- CheckoutResults: the post-payment return page (2026-09-16) ---
      //
      // Stripe redirects here after a hosted Checkout. It must be reachable in
      // exactly the two states the stages below refuse:
      //   - an org whose trial has lapsed — that is who is paying;
      //   - an org the webhook has already flipped to 'active' — that is who
      //     arrives when the webhook wins the race.
      // Before this branch the page had NEVER mounted, for anyone, in any
      // account state: the account check below bounced expired orgs, and the
      // permission check bounced everyone else because viewNameToRouteMap has
      // no entry producing 'CheckoutResults' and .filter(Boolean) drops it.
      //
      // Handled explicitly, like 'setup' and 'change-password' above, rather
      // than by either obvious alternative:
      //   - REMOVING beforeEnter would also drop isAuthenticated, the
      //     orgResolution tri-state, ensureFirebaseAuth() and the mint-403
      //     retired-member revocation. The page's own call needs that Firebase
      //     credential, so exempting it would not even work — and it would show
      //     a purchase confirmation to an unauthenticated stranger.
      //   - ADDING it to viewNameToRouteMap would need a "CheckoutResults" view
      //     name in every permission group in every live org (a data
      //     migration), and would let a role be configured unable to finish
      //     paying.
      //
      // Placed AFTER newUser and mustChangePassword deliberately: onboarding
      // and forced password changes still win. It skips only the account and
      // permission stages. See
      // tickets/retired/ticket-conversion-path-checkout-results-unreachable.md.
      if (to.name === 'CheckoutResults') {
        resolve(true);
        return;
      }

      // --- Account / trial status check ---

      let accountStatus;
      let trialDaysLeft = 0;

      try {
        const [, orgDoc] = await Promise.all([
          checkUserVerification(user.value),
          getDoc(doc(projectFirestore, `organisations/${store.organisationId}`))
        ]);

        accountStatus = orgDoc.data()?.accountStatus || "inactive";

        if (orgDoc.exists()) {
          const data = orgDoc.data();
          // Phase 1 (2026-08-17, trial-countdown correctness) — trialEndsAt
          // is the ONLY source (no legacy `trial` fallback — every org was
          // backfilled via scripts/backfill-trial-ends-at.mjs). `?? 0`
          // preserves this variable's original non-null "no trial info ->
          // treated as expired" default. See shared/trial's module header.
          trialDaysLeft = computeTrialDaysLeft(data.trialEndsAt, new Date()) ?? 0;
        }

        if (accountStatus === 'inactive' || (accountStatus === 'trial' && trialDaysLeft <= 0)) {
          const isAdmin = uT.permissions === 'Systems Administrator';
          const allowedRoute = isAdmin ? 'OrgSettings' : 'dashboard';
          if (to.name !== allowedRoute) {
            resolve({ name: allowedRoute, ...(isAdmin ? { query: { tab: 'plan' } } : {}) });
          } else {
            resolve(true);
          }
          return;
        }
      } catch (error) {
        // Membership-revocation fix (2026-08-24). F1 (retirePerson) now
        // arrayRemoves the person from organisations/{orgId}.uid, and
        // firestore.rules:42 gates the org document's own read on membership of
        // that very array — it is the ONLY read rule in the whole file that
        // does. So the getDoc above is the first thing that fails for a
        // just-retired person, and it fails with code 'permission-denied'.
        //
        // This branch used to `resolve(false)`. That produces a
        // NAVIGATION_ABORTED failure, and on the FIRST navigation of a page
        // load vue-router hands it to markAsReady(err), which rejects every
        // pending router.isReady() promise. App.vue gates the entire app shell
        // on that promise (`v-if="routerReady"`), so the abort rendered a
        // permanent blank page — no layout, no navbar, no route, unrecoverable
        // without a re-login — plus an unhandled rejection. A navigation guard
        // must always resolve to a real destination: there is deliberately no
        // longer any `resolve(false)` anywhere in this file.
        if (!firebaseBridgeFailed && error && error.code === 'permission-denied') {
          // Membership is genuinely gone. Downgrade the PERSISTED org state to
          // match reality BEFORE redirecting: noOrganisationGuard bounces
          // 'resolved-org' straight back to dashboard, so redirecting without
          // this would re-create the exact infinite guard cycle the 2026-08-08
          // orgless-user investigation existed to eliminate.
          trace('combined-guard-membership-revoked', { to: redactUrl(to.fullPath) });
          guardCache = null;
          store.organisationIdChange('');
          store.orgResolutionChange('resolved-none');
          uT.permissionsChange('');
          resolve({ name: 'no-organisation' });
          return;
        }
        console.error('Error checking account status in combinedGuard:', error);
        // Any other unrecoverable error (bridge down, network, verification
        // read) — send the user somewhere real to re-authenticate rather than
        // aborting the navigation into a blank shell.
        resolve({ name: 'login' });
        return;
      }

      // --- Per-route permission (userAccess) check ---

      try {
        const orgId = store.organisationId;
        const userPermissionName = uT.permissions;

        if (!orgId || !userPermissionName) {
          resolve(to.name === 'dashboard' ? true : { name: 'dashboard' });
          return;
        }

        const permissionsQuery = query(
          collection(projectFirestore, `organisations/${orgId}/permissions`),
          where("name", "==", userPermissionName)
        );

        const permissionsSnapshot = await getDocs(permissionsQuery);

        if (permissionsSnapshot.empty) {
          resolve(to.name === 'dashboard' ? true : { name: 'dashboard' });
          return;
        }

        const permissionData = permissionsSnapshot.docs[0].data();
        const allowedViews = permissionData.views || [];
        // flatMap, not map: a view may now name several routes (see the map).
        // `.filter(Boolean)` is gone because routeNamesForView already returns
        // [] for an unmapped view — keeping both would hide a map value of the
        // wrong SHAPE behind a filter that silently drops it.
        const allowedRoutes = allowedViews.flatMap(routeNamesForView);

        // All Firestore reads succeeded — populate the cache for future navigations
        guardCache = { newUser: false, mustChangePassword: false, accountStatus, trialDaysLeft, allowedRoutes };

        if (allowedRoutes.includes(to.name)) {
          resolve(true); // Explicitly allow — component may now mount
          return;
        }

        if (allowedRoutes.includes('dashboard')) {
          resolve({ name: 'dashboard' });
        } else if (allowedRoutes.length > 0) {
          resolve({ name: allowedRoutes[0] });
        } else {
          resolve({ name: 'login' });
        }
      } catch (error) {
        console.error('Error checking user access:', error);
        resolve({ name: 'dashboard' });
      }
    };

    // If Auth0 has already finished loading, run all checks immediately
    // without waiting for a watcher tick. If loading is still in progress,
    // watch for it to complete and then run checks.
    if (!isLoading.value) {
      runChecks();
    } else {
      const unwatch = watch(isLoading, (newValue) => {
        if (!newValue) {
          unwatch();
          runChecks();
        }
      });
    }
  });
};

// Orgless-user white-screen investigation (2026-08-08) — guards
// '/no-organisation' on isAuthenticated ONLY, deliberately never on org
// membership via a Firestore read (that's the entire reason this route
// exists: it IS the landing place for an authenticated user who has none).
// An unauthenticated direct visit bounces to login. Mirrors combinedGuard's
// own isLoading-wait shape (watch once if Auth0 hasn't settled yet, run
// immediately otherwise) without any of combinedGuard's Firestore checks —
// those are exactly what this route exists to skip.
//
// Tri-state fix (2026-08-09) — reads the persisted orgResolution rather
// than organisationId truthiness. 'resolved-org' bounces away (an org
// member must never sit on this screen); 'unknown' routes to post-login so
// the lookup that was never allowed to finish gets a chance to; only
// 'resolved-none' actually stays here.
export const noOrganisationGuard = (to) => {
  return new Promise((resolve) => {
    trace('no-organisation-guard-entered', { to: redactUrl(to.fullPath) });
    const { isAuthenticated, isLoading } = useAuth0();

    const check = async () => {
      await ensureSessionRestored();
      if (!isAuthenticated.value) {
        resolve({ name: 'login' });
        return;
      }
      const store = token();
      if (store.orgResolution === 'unknown') {
        resolve({ name: 'post-login' });
        return;
      }
      if (store.orgResolution === 'resolved-org') {
        resolve({ name: 'dashboard' });
        return;
      }
      resolve(true);
    };

    if (!isLoading.value) {
      check();
    } else {
      const unwatch = watch(isLoading, (newValue) => {
        if (!newValue) {
          unwatch();
          check();
        }
      });
    }
  });
};

const routes = [
  { path: '/',              name: 'login',         component: () => import('@/views/auth/Login.vue'),          meta: { layout: 'auth' } },
  { path: '/signup',        name: 'signup',         component: () => import('@/views/auth/Signup.vue'),         meta: { layout: 'auth' } },
  { path: '/verification',  name: 'verification',   component: () => import('@/views/auth/Verification.vue'),   meta: { layout: 'auth' } },
  // #69 (2026-08-04, Phase-A2) — landing route for the loginWithRedirect()
  // branch of startLogin(). Deliberately unguarded (no beforeEnter), same
  // as login/signup/verification above: org/status/permission checks must
  // not run before PostLogin.vue's own handleUserLogin() has even
  // established which org the user belongs to.
  { path: '/post-login',    name: 'post-login',     component: () => import('@/views/auth/PostLogin.vue'),      meta: { layout: 'auth' } },
  { path: '/no-organisation', name: 'no-organisation', component: () => import('@/views/auth/NoOrganisation.vue'), meta: { layout: 'auth' }, beforeEnter: noOrganisationGuard },
  { path: '/setup',         name: 'setup',          component: () => import('@/views/Setup.vue'),               meta: { layout: 'setup' },   beforeEnter: combinedGuard },
  { path: '/change-password', name: 'change-password', component: () => import('@/views/ChangePassword.vue'),   meta: { layout: 'setup' },   beforeEnter: combinedGuard },
  // ⚠ /comms IS THE CHAT (T7, 2026-10-28). The posts feed and `Comms.vue` are
  // gone; `/comms-chat` was the address the chat was built at while the feed
  // still owned `/comms`, and it redirects rather than 404s because it is in
  // browser histories, in the nav's capability map and in every screenshot of
  // the build so far.
  //
  // ⚠ THE REDIRECT KEEPS THE QUERY. A Dashboard row links to `?c=<convId>`, so
  // a redirect that dropped the query would land every stale link on the list
  // instead of the conversation it named — which is indistinguishable, on
  // screen, from the conversation having been deleted.
  { path: '/comms',         name: 'comms',          component: () => import('@/views/CommsChat.vue'),           meta: { layout: 'default' }, beforeEnter: combinedGuard },
  { path: '/comms-chat',    name: 'comms-chat',     redirect: (to) => ({ name: 'comms', query: to.query }) },
  // H1 (2026-11-01): the manual has the whole page; the contact form is a tab on it. The old
  // addresses keep working as redirects (a bookmarked /support, a link to /support/help/<chapter>).
  { path: '/help',          name: 'help',           component: () => import('@/views/Help.vue'),                meta: { layout: 'default' }, beforeEnter: combinedGuard },
  { path: '/help/:chapter', name: 'help-chapter',   component: () => import('@/views/Help.vue'),                meta: { layout: 'default' }, beforeEnter: combinedGuard },
  { path: '/support',       name: 'support',        redirect: { name: 'help' } },
  { path: '/support/help/:chapter', name: 'support-help', redirect: (to) => ({ name: 'help-chapter', params: { chapter: to.params.chapter } }) },
  { path: '/reports',       name: 'reports',        component: () => import('@/views/Reports.vue'),             meta: { layout: 'default' }, beforeEnter: combinedGuard },
  { path: '/timesheets',    name: 'timesheets',     component: () => import('@/views/Timesheets.vue'),          meta: { layout: 'default' }, beforeEnter: combinedGuard },
  { path: '/export-timesheets', name: 'export-timesheets', component: () => import('@/views/ExportTimesheets.vue'), meta: { layout: 'default' }, beforeEnter: combinedGuard },
  { path: '/send-to-xero', name: 'send-to-xero', component: () => import('@/views/SendToXero.vue'),      meta: { layout: 'default' }, beforeEnter: combinedGuard },
  // INTEGRATIONS — brief-integrations-page.md T1. Gated on the EXISTING
  // `Export Timesheets` view (D-C), which is why viewNameToRouteMap's value for
  // that view is an array. A stable path: phase 3's Xero mapping screen hangs
  // off it.
  { path: '/integrations',  name: 'integrations',   component: () => import('@/views/Integrations.vue'),        meta: { layout: 'default' }, beforeEnter: combinedGuard },
  // XERO SETUP — brief-xero-phase6c-polish-before-hugo.md T5. A THIRD surface of
  // the SAME `Export Timesheets` capability, which is why it is added to that
  // view's route list above rather than given a view name of its own: the
  // capability a venue grants is "may this person connect and feed a payroll
  // system", and splitting it would ask them a question they have no basis to
  // answer differently.
  //
  // ⚠ NO XERO-CONNECTION TEST IN THE GUARD, deliberately. `combinedGuard`
  // caches `allowedRoutes` for the session, so a connection check here would
  // bounce a venue that connects mid-session for the rest of it. A venue
  // arriving disconnected gets a sentence and a link back, rendered by the page.
  { path: '/integrations/xero', name: 'integrations-xero', component: () => import('@/views/XeroSetup.vue'),       meta: { layout: 'default' }, beforeEnter: combinedGuard },
  { path: '/dashboard',     name: 'dashboard',      component: () => import('@/views/Dashboard.vue'),           meta: { layout: 'default' }, beforeEnter: combinedGuard },
  { path: '/forms',         name: 'forms',          component: () => import('@/views/Forms.vue'),               meta: { layout: 'default' }, beforeEnter: combinedGuard },
  { path: '/templates',     name: 'templates',      component: () => import('@/views/Templates.vue'),           meta: { layout: 'default' }, beforeEnter: combinedGuard },
  { path: '/submitted',     name: 'submitted',      component: () => import('@/views/SubmittedForms.vue'),      meta: { layout: 'default' }, beforeEnter: combinedGuard },
  { path: '/OrgSettings',   name: 'OrgSettings',    component: () => import('@/views/OrgSettings.vue'),         meta: { layout: 'default' }, beforeEnter: combinedGuard },
  { path: '/schedule',      name: 'schedule',       component: () => import('@/views/Schedule.vue'),            meta: { layout: 'default' }, beforeEnter: combinedGuard },
  { path: '/people',        name: 'people',         component: () => import('@/views/People.vue'),              meta: { layout: 'default' }, beforeEnter: combinedGuard },
  { path: '/locations',     name: 'locations',      component: () => import('@/views/Locations.vue'),           meta: { layout: 'default' }, beforeEnter: combinedGuard },
  { path: '/checkout-results', name: 'CheckoutResults', component: () => import('@/views/CheckoutResults.vue'), meta: { layout: 'default' }, beforeEnter: combinedGuard },
  // idleTimeoutMs: shared-terminal routes (walk up, use, walk away) get a
  // shorter idle-logout threshold than back-office routes. See
  // SHARED_TERMINAL_IDLE_TIMEOUT_MS in useIdleLogout.js for rationale — a
  // pre-launch best guess, not tuned from real usage yet.
  { path: '/clock-in',      name: 'clock-in',       component: () => import('@/views/ClockInView.vue'),         meta: { layout: 'auth', idleTimeoutMs: SHARED_TERMINAL_IDLE_TIMEOUT_MS } },
  { path: '/my-shifts',     name: 'my-shifts',      component: () => import('@/views/MyShifts.vue'),            meta: { layout: 'auth', idleTimeoutMs: SHARED_TERMINAL_IDLE_TIMEOUT_MS } },
  { path: '/:catchAll(.*)', name: 'NotFound',       component: () => import('@/views/NotFound.vue'),            meta: { layout: 'default' }, beforeEnter: combinedGuard },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

// Z-campaign Phase 2 (2026-08-09) — REMOVED the router.push/router.replace
// monkey-patch that lived here (Dispatch Brief v2, Phase 2 2026-08-05,
// Addendum item 3). It wrapped the router instance permanently — the
// function replacement itself, not just its trace() call, was
// unconditionally installed regardless of debugEnabled, which is exactly
// the "always-installed" class of campaign scaffolding this pass is
// pruning. Its actual signal (did __checkSession's post-redirect push race
// the router's own first navigation) is already fully covered by the
// nav-start/nav-resolved hooks below: every router.push/replace call
// still triggers a real navigation, which still fires 'nav-start' with
// to.fullPath/from.fullPath, in order — the dual-navigation race is just
// as describable from that existing (already trace()-gated, no separate
// wrapping needed) pair as it was from the removed 'router.push-called'/
// 'router.replace-called' tags.

// Loop tripwire (2026-08-08, orgless-user white-screen investigation) —
// counts navigation attempts per route name in a rolling 5s window. Hooked
// into THIS beforeEach (nav-start) rather than instrumenting combinedGuard
// and sessionRestoreGuard separately: every guard bounce is itself a
// navigation, and this hook already fires exactly once per navigation
// attempt with the route name in hand — one chokepoint, not two. Detection
// only: never resolves/redirects/throws, never touches what any guard
// returns, so it cannot alter which route anyone lands on. Throttled to at
// most one 'guard-loop-detected' trace per window so the tripwire itself
// can't become a second flood once a real loop starts.
//
// Z-campaign Phase 2 audit (2026-08-09) — KEPT as a permanent canary, not
// campaign scaffolding. Field-proven, not just unit-tested: gcloud logging
// shows 12 confirmed 'guard-loop-detected' firings across Aug 8, including
// 22:23:16 during the captured loop — count 21, routeCounts login/
// dashboard, exactly to spec. The mechanism worked correctly in production
// conditions from the moment it was wired up (commit 6c2118a, same day as
// and before the actual fix ca23e38). The prior theory in this comment (a
// deploy-timing gap explaining an apparent absence of firings) was WRONG
// and has been disproven by direct gcloud read: the earlier "absence" was
// a log-retrieval window artifact (the query used to check was `-n 500`
// starting Aug 9, after the Aug 8 firings had already scrolled past that
// window) — not a fact about the code or the deploy. No bug ever existed
// here.
const GUARD_LOOP_WINDOW_MS = 5000;
const GUARD_LOOP_THRESHOLD = 20;
let guardLoopNavTimestamps = []; // [{ ts, route }]
let guardLoopLastTraceAt = 0;

export const __resetGuardLoopTripwireForTests = () => {
  guardLoopNavTimestamps = [];
  guardLoopLastTraceAt = 0;
};

export const recordGuardInvocation = (routeName, now = Date.now()) => {
  guardLoopNavTimestamps.push({ ts: now, route: routeName });
  guardLoopNavTimestamps = guardLoopNavTimestamps.filter((entry) => now - entry.ts <= GUARD_LOOP_WINDOW_MS);

  if (guardLoopNavTimestamps.length > GUARD_LOOP_THRESHOLD && now - guardLoopLastTraceAt > GUARD_LOOP_WINDOW_MS) {
    guardLoopLastTraceAt = now;
    const routeCounts = guardLoopNavTimestamps.reduce((acc, entry) => {
      acc[entry.route] = (acc[entry.route] || 0) + 1;
      return acc;
    }, {});
    trace('guard-loop-detected', { routeCounts, windowMs: GUARD_LOOP_WINDOW_MS, count: guardLoopNavTimestamps.length });
    return true;
  }
  return false;
};

// Dead-man's-switch boot-healthy marker (2026-08-09, Z-campaign Phase 1,
// Defect B) — settle timer armed on every resolved navigation, cleared on
// every new navigation attempt. Only fires (and only then calls
// markBootHealthy()) once NAV_SETTLE_QUIET_MS passes with no further
// nav-start — i.e. the navigation actually landed, not just resolved.
// During the confirmed guard-race loop, nav-resolved fired continuously
// (failure: null, on both '/' and '/dashboard') right up until the next
// bounce; bare resolution alone would have marked a looping boot healthy.
// Resolve-plus-quiet-window is the actual signature of a landed boot —
// covers fresh-login and session-restored paths alike, and needs no route
// allowlist to keep in sync with the route table.
const NAV_SETTLE_QUIET_MS = 1000;
let navSettleTimer = null;

export const __resetNavSettleWatchdogForTests = () => {
  if (navSettleTimer) clearTimeout(navSettleTimer);
  navSettleTimer = null;
};

// Exported (not just registered inline) so the settle behaviour is directly
// unit-testable, same rationale as recordGuardInvocation's own export above.
export const navStartHandler = (to, from) => {
  trace('nav-start', {
    to: redactUrl(to.fullPath),
    from: redactUrl(from.fullPath) || '(initial)',
    hasCode: to.fullPath.includes('code='),
  });
  recordGuardInvocation(to.name || '(unnamed)');
  if (navSettleTimer) {
    clearTimeout(navSettleTimer);
    navSettleTimer = null;
  }
};

export const navResolvedHandler = (to, from, failure) => {
  trace('nav-resolved', {
    to: redactUrl(to.fullPath),
    from: redactUrl(from.fullPath) || '(initial)',
    failure: failure ? failure.type : null,
  });
  if (navSettleTimer) clearTimeout(navSettleTimer);
  navSettleTimer = setTimeout(() => {
    navSettleTimer = null;
    markBootHealthy();
  }, NAV_SETTLE_QUIET_MS);
};

router.beforeEach(navStartHandler);
router.afterEach(navResolvedHandler);

// #73 (2026-08-03): combinedGuard is only ever wired up via per-route
// beforeEnter — and the login/signup/verification routes deliberately don't
// have one (org/status/permission checks must not run on them: a mid-signup
// or mid-email-verification user must not get bounced around by checks that
// assume a fully set-up org). That's correct for those checks, but it means
// ensureSessionRestored() — which only needs isAuthenticated, nothing
// org-specific — never ran at all when the app's first navigation lands on
// an unguarded route. And it always does: public/manifest.json's
// start_url is "/", which is the login route, so every cold boot of the
// installed app (and every plain tab/window reopen) resolves there first.
// This global hook runs the restore probe unconditionally, on every
// navigation, deliberately kept to ONLY that — no org/status/permission
// checks are added here, so signup/verification are unaffected beyond the
// one-line no-op (the flag inside ensureSessionRestored makes every call
// after the very first a synchronous return).
//
// The bounce below is the other half: without it, a successfully restored
// session just leaves the user looking at the login screen anyway, since
// nothing here or in combinedGuard re-navigates on isAuthenticated flipping
// true after the fact. Scoped to the login route only, on purpose — signup
// and verification keep their current behaviour untouched, since an
// authenticated-but-mid-verification user bouncing to /dashboard and then
// (if still unverified) back again would be a redirect loop, not a fix.
// Deep links to guarded routes need no bounce here at all: combinedGuard's
// own ensureSessionRestored() call (a free no-op via the same flag) already
// runs before its isAuthenticated check on every one of those navigations.
// Exported so it can be unit-tested directly, without booting the full
// router/Pinia/Firestore chain that a real navigation through combinedGuard
// would require.
//
// Orgless-user white-screen investigation (2026-08-08) — this bounce is now
// org-aware, not just auth-aware. Before this fix it always sent an
// authenticated user to 'dashboard' unconditionally; for an orgless user
// that landed on combinedGuard's beforeEnter (dashboard is guarded), whose
// own !store.organisationId check sent them straight back to 'login' —
// and THIS hook fired again on that very navigation, since it only checked
// isAuthenticated, sending them back to 'dashboard' again. Infinite. The
// fix: read org state here too, and terminate on 'no-organisation' instead
// of ever producing a second bounce. 'no-organisation' itself is guarded by
// noOrganisationGuard (isAuthenticated only, see routes below) — nothing
// downstream sends an authenticated user away from it, so this can never
// manufacture a NEW two-guard cycle the way the old unconditional bounce
// did.
export const sessionRestoreGuard = async (to) => {
  trace('session-restore-guard-entered', { to: redactUrl(to.fullPath) });
  await ensureSessionRestored();

  if (to.name === 'login' && auth0.isAuthenticated.value) {
    const store = token();
    // Tri-state fix (2026-08-09) — see combinedGuard's own comment. This is
    // the exact race the tri-state fix targets: this navigation (to
    // 'login') can still be in flight, awaiting ensureSessionRestored above,
    // when a fresh redirect-callback login lands and flips isAuthenticated
    // true — BEFORE handleUserLogin has run its org lookup. Reading
    // organisationId here used to make that transient '' look identical to
    // a real "resolved: no org", terminally misrouting an authenticated
    // user (including one with a real org) to no-organisation.
    if (store.orgResolution === 'unknown') {
      trace('session-restore-guard-bounce-to-post-login');
      return { name: 'post-login' };
    }
    if (store.orgResolution === 'resolved-none') {
      trace('session-restore-guard-bounce-to-no-organisation');
      return { name: 'no-organisation' };
    }
    trace('session-restore-guard-bounce-to-dashboard');
    return { name: 'dashboard' };
  }
};

router.beforeEach(sessionRestoreGuard);

// Block the Schedule page on small screens and surface the "screen too small"
// popup. The guard's reactive state (showPopup) lives at module scope in
// screenGuard.js, so it is shared with the instance App.vue reads to render the
// popup. Registered here rather than per-route because it is a cross-cutting
// concern that must run before the route's own beforeEnter guard.
const { screenGuard } = useScreenGuard();
router.beforeEach(screenGuard);

export default router;
